import { useListContext } from "ra-core";
import { useNavigate } from "react-router";
import { Ban, Handshake, Users } from "lucide-react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { LeadBadge } from "./CompanyLead";
import { CallLink } from "../calls/CallTracker";

// LDF: Firmen als kompakte Liste (Alternative zu den Kacheln). Zeilenhöhe fest, damit die Seitengröße zur Fensterhöhe passt.
export const COMPANY_ROW_HEIGHT = 44;

export const CompanyTable = () => {
  const { data, isPending } = useListContext<Company>();
  const { companySectors } = useConfigurationContext();
  const navigate = useNavigate();
  if (isPending) return null;

  return (
    <div className="h-full overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="sticky top-0 bg-background">
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="py-2 pl-2 pr-3 font-medium">Name</th>
            <th className="py-2 pr-3 font-medium">Typ</th>
            <th className="py-2 pr-3 font-medium">Akquise</th>
            <th className="py-2 pr-3 font-medium">Ort</th>
            <th className="py-2 pr-3 font-medium">Telefon</th>
            <th className="py-2 pr-3 text-right font-medium" title="Kontakte">
              <Users className="ml-auto h-3.5 w-3.5" />
            </th>
            <th className="py-2 pr-2 text-right font-medium" title="Aufträge">
              <Handshake className="ml-auto h-3.5 w-3.5" />
            </th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((c) => (
            <tr
              key={c.id}
              onClick={() => navigate(`/companies/${c.id}/show`)}
              className="cursor-pointer border-b border-border hover:bg-muted"
              style={{ height: COMPANY_ROW_HEIGHT }}
            >
              <td className="max-w-72 py-1 pl-2 pr-3">
                <div className="flex items-center gap-2">
                  <CompanyAvatar record={c} width={20} height={20} />
                  <span className={`truncate font-medium ${c.nb_deals ? "text-blue-600 dark:text-blue-400" : ""}`} title={c.name}>
                    {c.name}
                  </span>
                  {c.contact_blocked && (
                    <span title="Kontaktsperre">
                      <Ban className="h-3.5 w-3.5 shrink-0 text-destructive" />
                    </span>
                  )}
                </div>
              </td>
              <td className="max-w-40 truncate py-1 pr-3 text-muted-foreground">
                {companySectors.find((s) => s.value === c.sector)?.label ?? c.sector}
              </td>
              <td className="py-1 pr-3">{c.lead_score && c.lead_score !== "skip" ? <LeadBadge score={c.lead_score} /> : null}</td>
              <td className="max-w-48 truncate py-1 pr-3 text-muted-foreground" title={[c.address, c.zipcode, c.city].filter(Boolean).join(", ")}>
                {[c.district, c.city].filter(Boolean).join(" · ")}
              </td>
              <td className="whitespace-nowrap py-1 pr-3">
                {c.phone_number && !c.contact_blocked ? (
                  <CallLink number={c.phone_number} name={c.name} companyId={c.id} />
                ) : null}
              </td>
              <td className="py-1 pr-3 text-right text-muted-foreground">{c.nb_contacts || ""}</td>
              <td className="py-1 pr-2 text-right">{c.nb_deals || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
